import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Boton } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Calificacion {
  id: string;
  /** Nulo en una encomienda: no hay comercio a quien calificar. */
  merchantScore: number | null;
  courierScore: number | null;
  comment: string | null;
  merchantReply: string | null;
}

/**
 * Calificar el pedido, en la misma pantalla donde se sigue.
 *
 * Sin pantalla aparte ni correo con enlace: el momento en que alguien está
 * dispuesto a calificar es justo cuando acaba de recibir, mirando el pedido.
 * Cualquier paso de más y no lo hace nadie.
 */
/**
 * En una encomienda solo se califica al domiciliario.
 *
 * `comercio` llega nulo y entonces la nota que manda es la suya: exigir una
 * nota de comercio que no existe dejaría el botón deshabilitado para siempre y
 * al domiciliario sin reputación en la mitad de sus encargos —justo la que
 * decide a quién se le ofrece el siguiente—.
 */
export function Calificar({
  orderId,
  comercio,
  hayDomiciliario,
}: {
  orderId: string;
  comercio: string | null;
  hayDomiciliario: boolean;
}) {
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [comercioEstrellas, setComercioEstrellas] = useState(0);
  const [domiEstrellas, setDomiEstrellas] = useState(0);
  const [comentario, setComentario] = useState('');

  const existente = useQuery({
    queryKey: ['calificacion', orderId],
    queryFn: async () =>
      (await api.get<Calificacion | null>(`/pedidos/${orderId}/calificacion`)).data,
  });

  const enviar = useMutation({
    mutationFn: async () =>
      (
        await api.post<Calificacion>(`/pedidos/${orderId}/calificar`, {
          // El servidor la ignora si no hay comercio, pero el esquema la exige.
          merchantScore: comercio ? comercioEstrellas : 5,
          ...(hayDomiciliario && domiEstrellas > 0 ? { courierScore: domiEstrellas } : {}),
          ...(comentario.trim() ? { comment: comentario.trim() } : {}),
        })
      ).data,
    onSuccess: () => {
      avisos.exito(
        comercio ? '¡Gracias! Tu opinión ayuda al comercio.' : '¡Gracias por calificar!',
      );
      void cliente.invalidateQueries({ queryKey: ['calificacion', orderId] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  /** Qué nota hace falta para poder enviar, según haya comercio o no. */
  const falta = comercio ? comercioEstrellas === 0 : domiEstrellas === 0;

  if (existente.isLoading) return null;

  // ── Ya calificado ──
  if (existente.data) {
    return (
      <section className="card-kick mt-4 p-5">
        <h2 className="text-lg text-kick-dark">Tu calificación</h2>
        {comercio && existente.data.merchantScore != null && (
          <Estrellas valor={existente.data.merchantScore} soloLectura etiqueta={comercio} />
        )}

        {existente.data.courierScore != null && (
          <div className="mt-2">
            <Estrellas
              valor={existente.data.courierScore}
              soloLectura
              etiqueta="El domiciliario"
            />
          </div>
        )}

        {existente.data.comment && (
          <p className="mt-3 rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">
            {existente.data.comment}
          </p>
        )}

        {comercio && existente.data.merchantReply && (
          <div className="mt-3 rounded-kick border-l-4 border-kick-red bg-kick-light px-3 py-2">
            <p className="text-xs font-bold uppercase tracking-wide text-kick-gray">
              Respuesta de {comercio}
            </p>
            <p className="mt-0.5 text-sm text-kick-ink">{existente.data.merchantReply}</p>
          </div>
        )}
      </section>
    );
  }

  // ── Sin calificar ──
  return (
    <section className="card-kick mt-4 p-5">
      <h2 className="text-lg text-kick-dark">¿Qué tal estuvo?</h2>
      <p className="mt-1 text-sm text-kick-gray">
        Tu opinión es pública y ayuda a quien pide después.
      </p>

      <div className="mt-4 space-y-4">
        {comercio && (
          <Estrellas
            valor={comercioEstrellas}
            onCambio={setComercioEstrellas}
            etiqueta={comercio}
          />
        )}

        {hayDomiciliario && (
          <Estrellas
            valor={domiEstrellas}
            onCambio={setDomiEstrellas}
            etiqueta="El domiciliario"
            // En una encomienda es la única nota: deja de ser opcional.
            opcional={!!comercio}
          />
        )}

        <div>
          <label htmlFor="comentario" className="text-sm font-semibold text-kick-ink">
            Cuéntanos <span className="font-normal text-kick-gray">(opcional)</span>
          </label>
          <textarea
            id="comentario"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Llegó caliente y rapidísimo…"
            className="mt-1.5 w-full rounded-kick border border-kick-gray/30 px-3 py-2 text-sm text-kick-ink outline-none transition focus:border-kick-red"
          />
        </div>

        <Boton
          className="w-full"
          onClick={() => enviar.mutate()}
          cargando={enviar.isPending}
          disabled={falta}
        >
          {falta ? 'Elige cuántas estrellas' : 'Enviar calificación'}
        </Boton>
      </div>
    </section>
  );
}

function Estrellas({
  valor,
  onCambio,
  etiqueta,
  soloLectura,
  opcional,
}: {
  valor: number;
  onCambio?: (v: number) => void;
  etiqueta: string;
  soloLectura?: boolean;
  opcional?: boolean;
}) {
  return (
    <div>
      <p className="text-sm font-semibold text-kick-ink">
        {etiqueta}
        {opcional && <span className="font-normal text-kick-gray"> (opcional)</span>}
      </p>
      <div
        className="mt-1 flex gap-1"
        role={soloLectura ? undefined : 'radiogroup'}
        aria-label={soloLectura ? undefined : `Calificar ${etiqueta}`}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={soloLectura}
            onClick={() => onCambio?.(n)}
            role={soloLectura ? undefined : 'radio'}
            aria-checked={soloLectura ? undefined : valor === n}
            aria-label={`${n} ${n === 1 ? 'estrella' : 'estrellas'}`}
            className={cn(
              'rounded p-0.5 transition',
              !soloLectura && 'hover:scale-110 active:scale-95',
            )}
          >
            <Star
              className={cn(
                'h-7 w-7',
                n <= valor ? 'fill-amber-400 text-amber-400' : 'text-kick-gray/40',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
